export enum EquipmentType {
  WEAPON = 'weapon',
  ARMOR = 'armor',
  ACCESSORY = 'accessory',
  SPECIAL = 'special',
  PET = 'pet',
  EARTH = 'earth',
  TOWER = 'tower'
}

export enum EquipmentRarity {
  COMMON = 'common',
  RARE = 'rare',
  EPIC = 'epic',
  LEGENDARY = 'legendary'
}

export interface EquipmentStats {
  // Combat stats
  damage?: number;          // Tăng sát thương tháp
  attackSpeed?: number;     // Tăng tốc độ bắn
  range?: number;           // Tăng tầm bắn
  critChance?: number;      // Tỷ lệ chí mạng
  critDamage?: number;      // Sát thương chí mạng
  
  // Earth stats
  earthHealth?: number;     // Tăng máu Earth
  earthDefense?: number;    // Tăng kháng sát thương Earth
  energyRegen?: number;     // Tăng tốc độ tái tạo energy
  
  // Economy stats
  goldBonus?: number;       // Tăng % vàng nhận được
  rubyBonus?: number;       // Tăng % ruby nhận được
  xpBonus?: number;         // Tăng % XP nhận được
  
  // Special stats
  projectileCount?: number; // Tăng số đạn bắn cùng lúc
  pierceCount?: number;     // Tăng số quái xuyên thấu
  splashRadius?: number;    // Tăng bán kính nổ
  freezeChance?: number;    // Tỷ lệ làm băng quái
  burnChance?: number;      // Tỷ lệ đốt cháy quái
}

export interface EquipmentConfig {
  id: string;
  name: string;
  description: string;
  type: EquipmentType;
  rarity: EquipmentRarity;
  level: number;
  maxLevel: number;
  baseStats: EquipmentStats;
  upgradeStats: EquipmentStats; // Stats tăng mỗi level
  cost: number;                 // Giá mua
  upgradeCost: number;          // Giá nâng cấp base
  currency: 'gold' | 'ruby';
  unlockLevel?: number;         // Level cần thiết để mở khóa
}

// Weapons - Tăng sát thương, tốc độ, đặc biệt
export const WEAPON_CONFIGS: EquipmentConfig[] = [
  {
    id: 'basic_cannon',
    name: 'Basic Cannon',
    description: 'Standard weapon upgrade',
    type: EquipmentType.WEAPON,
    rarity: EquipmentRarity.COMMON,
    level: 1,
    maxLevel: 10,
    baseStats: { damage: 10, attackSpeed: 0.1 },
    upgradeStats: { damage: 5, attackSpeed: 0.05 },
    cost: 100,
    upgradeCost: 50,
    currency: 'gold'
  },
  {
    id: 'rapid_blaster',
    name: 'Rapid Blaster',
    description: 'High attack speed weapon',
    type: EquipmentType.WEAPON,
    rarity: EquipmentRarity.RARE,
    level: 1,
    maxLevel: 10,
    baseStats: { attackSpeed: 0.3, damage: 5 },
    upgradeStats: { attackSpeed: 0.1, damage: 3 },
    cost: 300,
    upgradeCost: 100,
    currency: 'gold',
    unlockLevel: 5
  },
  {
    id: 'piercing_rifle',
    name: 'Piercing Rifle',
    description: 'Shoots through multiple enemies',
    type: EquipmentType.WEAPON,
    rarity: EquipmentRarity.EPIC,
    level: 1,
    maxLevel: 8,
    baseStats: { damage: 15, pierceCount: 2, range: 20 },
    upgradeStats: { damage: 8, pierceCount: 1, range: 10 },
    cost: 500,
    upgradeCost: 200,
    currency: 'gold',
    unlockLevel: 10
  },
  {
    id: 'crit_cannon',
    name: 'Critical Cannon',
    description: 'High critical hit chance and damage',
    type: EquipmentType.WEAPON,
    rarity: EquipmentRarity.LEGENDARY,
    level: 1,
    maxLevel: 5,
    baseStats: { damage: 20, critChance: 0.15, critDamage: 1.5 },
    upgradeStats: { damage: 15, critChance: 0.05, critDamage: 0.3 },
    cost: 50,
    upgradeCost: 50,
    currency: 'ruby',
    unlockLevel: 15
  }
];

// Armor - Tăng phòng thủ Earth, máu, kháng
export const ARMOR_CONFIGS: EquipmentConfig[] = [
  {
    id: 'basic_shield',
    name: 'Basic Shield',
    description: 'Standard defense upgrade',
    type: EquipmentType.ARMOR,
    rarity: EquipmentRarity.COMMON,
    level: 1,
    maxLevel: 10,
    baseStats: { earthHealth: 100, earthDefense: 0.05 },
    upgradeStats: { earthHealth: 50, earthDefense: 0.02 },
    cost: 120,
    upgradeCost: 60,
    currency: 'gold'
  },
  {
    id: 'reinforced_armor',
    name: 'Reinforced Armor',
    description: 'Heavy protection for Earth',
    type: EquipmentType.ARMOR,
    rarity: EquipmentRarity.RARE,
    level: 1,
    maxLevel: 10,
    baseStats: { earthHealth: 200, earthDefense: 0.1 },
    upgradeStats: { earthHealth: 100, earthDefense: 0.03 },
    cost: 350,
    upgradeCost: 120,
    currency: 'gold',
    unlockLevel: 5
  },
  {
    id: 'energy_barrier',
    name: 'Energy Barrier',
    description: 'Generates energy while protecting',
    type: EquipmentType.ARMOR,
    rarity: EquipmentRarity.EPIC,
    level: 1,
    maxLevel: 8,
    baseStats: { earthHealth: 150, energyRegen: 5 },
    upgradeStats: { earthHealth: 80, energyRegen: 3 },
    cost: 600,
    upgradeCost: 250,
    currency: 'gold',
    unlockLevel: 12
  },
  {
    id: 'quantum_shield',
    name: 'Quantum Shield',
    description: 'Ultimate defense system',
    type: EquipmentType.ARMOR,
    rarity: EquipmentRarity.LEGENDARY,
    level: 1,
    maxLevel: 5,
    baseStats: { earthHealth: 300, earthDefense: 0.2, energyRegen: 10 },
    upgradeStats: { earthHealth: 200, earthDefense: 0.05, energyRegen: 5 },
    cost: 80,
    upgradeCost: 80,
    currency: 'ruby',
    unlockLevel: 20
  }
];

// Accessories - Tăng economy, đặc biệt
export const ACCESSORY_CONFIGS: EquipmentConfig[] = [
  {
    id: 'gold_magnet',
    name: 'Gold Magnet',
    description: 'Increases gold gain from monsters',
    type: EquipmentType.ACCESSORY,
    rarity: EquipmentRarity.COMMON,
    level: 1,
    maxLevel: 10,
    baseStats: { goldBonus: 0.1 },
    upgradeStats: { goldBonus: 0.05 },
    cost: 80,
    upgradeCost: 40,
    currency: 'gold'
  },
  {
    id: 'experience_boost',
    name: 'Experience Boost',
    description: 'Gain more XP from all sources',
    type: EquipmentType.ACCESSORY,
    rarity: EquipmentRarity.RARE,
    level: 1,
    maxLevel: 10,
    baseStats: { xpBonus: 0.15 },
    upgradeStats: { xpBonus: 0.08 },
    cost: 250,
    upgradeCost: 80,
    currency: 'gold',
    unlockLevel: 3
  },
  {
    id: 'lucky_charm',
    name: 'Lucky Charm',
    description: 'Increases all bonuses slightly',
    type: EquipmentType.ACCESSORY,
    rarity: EquipmentRarity.EPIC,
    level: 1,
    maxLevel: 8,
    baseStats: { goldBonus: 0.1, rubyBonus: 0.1, xpBonus: 0.1 },
    upgradeStats: { goldBonus: 0.05, rubyBonus: 0.05, xpBonus: 0.05 },
    cost: 450,
    upgradeCost: 180,
    currency: 'gold',
    unlockLevel: 8
  },
  {
    id: 'ruby_generator',
    name: 'Ruby Generator',
    description: 'Significantly boosts ruby income',
    type: EquipmentType.ACCESSORY,
    rarity: EquipmentRarity.LEGENDARY,
    level: 1,
    maxLevel: 5,
    baseStats: { rubyBonus: 0.5, goldBonus: 0.2 },
    upgradeStats: { rubyBonus: 0.2, goldBonus: 0.1 },
    cost: 30,
    upgradeCost: 30,
    currency: 'ruby',
    unlockLevel: 18
  }
];

// Special items - Hiệu ứng đặc biệt
export const SPECIAL_CONFIGS: EquipmentConfig[] = [
  {
    id: 'freeze_core',
    name: 'Freeze Core',
    description: 'Chance to freeze enemies on hit',
    type: EquipmentType.SPECIAL,
    rarity: EquipmentRarity.RARE,
    level: 1,
    maxLevel: 10,
    baseStats: { freezeChance: 0.1, damage: 5 },
    upgradeStats: { freezeChance: 0.03, damage: 3 },
    cost: 400,
    upgradeCost: 150,
    currency: 'gold',
    unlockLevel: 7
  },
  {
    id: 'explosive_module',
    name: 'Explosive Module',
    description: 'Adds splash damage to attacks',
    type: EquipmentType.SPECIAL,
    rarity: EquipmentRarity.EPIC,
    level: 1,
    maxLevel: 8,
    baseStats: { splashRadius: 30, damage: 10 },
    upgradeStats: { splashRadius: 10, damage: 8 },
    cost: 550,
    upgradeCost: 220,
    currency: 'gold',
    unlockLevel: 12
  },
  {
    id: 'multishot_core',
    name: 'Multishot Core',
    description: 'Fires multiple projectiles',
    type: EquipmentType.SPECIAL,
    rarity: EquipmentRarity.LEGENDARY,
    level: 1,
    maxLevel: 5,
    baseStats: { projectileCount: 2, damage: 15 },
    upgradeStats: { projectileCount: 1, damage: 10 },
    cost: 100,
    upgradeCost: 100,
    currency: 'ruby',
    unlockLevel: 25
  }
];

// Tất cả equipment configs
export const ALL_EQUIPMENT_CONFIGS: EquipmentConfig[] = [
  ...WEAPON_CONFIGS,
  ...ARMOR_CONFIGS,
  ...ACCESSORY_CONFIGS,
  ...SPECIAL_CONFIGS
];

// Helper functions
export function getEquipmentById(id: string): EquipmentConfig | undefined {
  return ALL_EQUIPMENT_CONFIGS.find(eq => eq.id === id);
}

export function getEquipmentsByType(type: EquipmentType): EquipmentConfig[] {
  return ALL_EQUIPMENT_CONFIGS.filter(eq => eq.type === type);
}

export function getEquipmentsByRarity(rarity: EquipmentRarity): EquipmentConfig[] {
  return ALL_EQUIPMENT_CONFIGS.filter(eq => eq.rarity === rarity);
}

export function calculateUpgradeCost(equipment: EquipmentConfig, currentLevel: number): number {
  return Math.floor(equipment.upgradeCost * Math.pow(1.5, currentLevel - 1));
}

export function calculateCurrentStats(equipment: EquipmentConfig, currentLevel: number): EquipmentStats {
  const stats: EquipmentStats = { ...equipment.baseStats };
  const levelMultiplier = currentLevel - 1;
  
  // Cộng stats theo level
  Object.keys(equipment.upgradeStats).forEach(key => {
    const statKey = key as keyof EquipmentStats;
    if (stats[statKey] !== undefined && equipment.upgradeStats[statKey] !== undefined) {
      (stats[statKey] as number) += (equipment.upgradeStats[statKey] as number) * levelMultiplier;
    }
  });
  
  return stats;
}

// Rarity colors for UI
export const RARITY_COLORS = {
  [EquipmentRarity.COMMON]: '#ffffff',
  [EquipmentRarity.RARE]: '#0099ff',
  [EquipmentRarity.EPIC]: '#9966ff',
  [EquipmentRarity.LEGENDARY]: '#ff9900'
};

// Equipment slot limits
export const EQUIPMENT_SLOTS = {
  [EquipmentType.WEAPON]: 2,     // Có thể trang bị 2 weapon
  [EquipmentType.ARMOR]: 1,      // Chỉ 1 armor
  [EquipmentType.ACCESSORY]: 3,  // Tối đa 3 accessory
  [EquipmentType.SPECIAL]: 1     // Chỉ 1 special
}; 